import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Upload, Loader2, ShieldCheck, LogOut, ImagePlus, RefreshCw } from 'lucide-react';
import { adminApi, logoutAdmin } from './adminApi';
import { toAssetUrl } from '@/data/runtimeApi';
import { uploadFiles } from './uploadInBatches';

const toUrl = toAssetUrl;

export default function AdminNosotros() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);   // { inserted, errors, failedFiles }
  const [progress, setProgress] = useState(null);
  const [reordering, setReordering] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/nosotros');
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    if (reordering) return;

    const to = idx + dir;
    if (to < 0 || to >= items.length) return;

    const previousItems = items;
    const nextItems = [...items];
    [nextItems[idx], nextItems[to]] = [nextItems[to], nextItems[idx]];

    setItems(nextItems);
    setReordering(true);

    try {
      const { data } = await adminApi.post(
        '/nosotros/reorder',
        nextItems.map((item) => item.id),
      );

      // Use the list returned by the backend as the single source of truth.
      if (Array.isArray(data?.items)) {
        setItems(data.items);
      }
    } catch (e) {
      setItems(previousItems);
      alert(e?.response?.data?.detail || 'No se pudo guardar el nuevo orden');
    } finally {
      setReordering(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta foto? Esta acción no se puede deshacer.')) return;
    try {
      const { data } = await adminApi.delete(`/nosotros/${id}`);
      // Trust server response - avoids stale-refetch bug on CDN/production
      setItems(data.items || []);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`¿Eliminar TODAS las ${items.length} fotos de la galería Nosotros?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      const { data } = await adminApi.delete('/nosotros');
      setItems(data.items || []);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  const runUpload = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return;
    setUploading(true);
    setProgress({ completed: 0, total: filesToUpload.length, currentName: null, phase: 'compress' });

    const result = await uploadFiles(
      filesToUpload,
      async (blob, name /*, originalFile */) => {
        const fd = new FormData();
        fd.append('files', blob, name);
        const { data } = await adminApi.post('/nosotros/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        return data;
      },
      (p) => {
        setProgress(p);
        // Server always returns the authoritative full `items` list - trust it.
        if (p.lastData && Array.isArray(p.lastData.items)) {
          setItems(p.lastData.items);
        }
      },
    );

    setUploadInfo({
      inserted: result.inserted,
      errors: result.errors,
      failedFiles: result.failedFiles,
    });
    setUploading(false);
    setProgress(null);
  };

  const upload = async () => {
    const files = fileRef.current?.files;
    if (!files || files.length === 0) { alert('Selecciona una o varias imágenes'); return; }
    setUploadInfo(null);
    const arr = Array.from(files);
    if (fileRef.current) fileRef.current.value = '';
    await runUpload(arr);
  };

  const retryFailed = async () => {
    if (!uploadInfo?.failedFiles?.length) return;
    const retryList = uploadInfo.failedFiles;
    setUploadInfo(null);
    await runUpload(retryList);
  };

  const onLogout = () => { logoutAdmin(); window.location.href = '/admin/login'; };

  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="admin-nosotros-page">
      <header className="bg-white border-b border-[#58677A]/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#58677A] hover:text-[#ED6B24] transition"><ArrowLeft size={18}/></Link>
            <div className="w-9 h-9 rounded-lg bg-[#ED6B24]/10 flex items-center justify-center"><ShieldCheck className="text-[#ED6B24]" size={18} /></div>
            <div>
              <p className="text-[15px] font-bold text-[#58677A] leading-tight" style={{fontFamily:'Gotham,sans-serif'}}>Galería Nosotros</p>
              <p className="text-[11px] text-[#58677A]/60">Página /nosotros</p>
            </div>
          </div>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-xs text-[#58677A] hover:text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50" data-testid="admin-logout"><LogOut size={13}/> Salir</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Upload */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6">
          <h2 className="font-bold text-[#58677A] mb-1 flex items-center gap-2" style={{fontFamily:'Gotham,sans-serif'}}>
            <ImagePlus size={18} className="text-[#ED6B24]"/> Subir fotos
          </h2>
          <p className="text-xs text-[#58677A]/60 mb-4">Puedes seleccionar múltiples imágenes a la vez (incluso 100+). Se comprimen en tu navegador y se suben una por una para máxima estabilidad.</p>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              className="w-full text-sm text-[#58677A] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#ED6B24]/10 file:text-[#ED6B24] file:font-semibold file:cursor-pointer disabled:opacity-60"
              data-testid="nosotros-files-input"
            />
            <button
              onClick={upload}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-lg px-5 py-2.5 disabled:opacity-60"
              data-testid="upload-nosotros-btn"
            >
              {uploading ? <Loader2 size={15} className="animate-spin"/> : <Upload size={15}/>}
              {uploading ? 'Subiendo…' : 'Subir seleccionadas'}
            </button>
          </div>

          {progress && progress.total > 0 && (
            <div className="mt-4" data-testid="upload-progress">
              <div className="flex items-center justify-between text-xs text-[#58677A] mb-1.5">
                <span className="font-semibold">
                  {progress.phase === 'compress' ? 'Optimizando' : 'Subiendo'} {progress.completed} de {progress.total}
                  {progress.currentName && (
                    <span className="text-[#58677A]/60 font-normal font-mono ml-1">· {progress.currentName.length > 32 ? progress.currentName.slice(0, 32) + '…' : progress.currentName}</span>
                  )}
                </span>
                <span className="tabular-nums">{Math.round((progress.completed / progress.total) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-[#f4f5f7] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#ED6B24] transition-[width] duration-300"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
              {progress.errors > 0 && (
                <p className="text-[11px] text-red-600 mt-1">{progress.errors} error(es) hasta ahora - el resto continúa.</p>
              )}
            </div>
          )}
          {uploadInfo && (
            <div className="mt-4 text-xs bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2" data-testid="upload-result">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  ✓ {uploadInfo.inserted} imagen(es) subidas y optimizadas.
                  {uploadInfo.errors.length > 0 && (
                    <div className="text-red-600 mt-1">
                      {uploadInfo.errors.length} error(es):
                      <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                        {uploadInfo.errors.map((e, i) => (
                          <li key={i} className="text-[11px]"><span className="font-mono">{e.name}</span>: {e.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {uploadInfo.failedFiles?.length > 0 && !uploading && (
                  <button
                    onClick={retryFailed}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-3 py-1.5"
                    data-testid="retry-failed-btn"
                  >
                    <RefreshCw size={12}/> Reintentar {uploadInfo.failedFiles.length}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* List */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10">
          <div className="px-5 sm:px-6 py-4 border-b border-[#58677A]/10 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Fotos actuales ({items.length})</h2>
              <p className="text-xs text-[#58677A]/60 mt-0.5">Usa las flechas para reordenar. El orden se refleja en la web al instante.</p>
            </div>
            {items.length > 0 && (
              <button onClick={clearAll} className="inline-flex items-center gap-1.5 text-red-600 border border-red-300 hover:bg-red-500 hover:text-white text-xs font-semibold rounded-md px-3 py-2" data-testid="clear-nosotros-btn">
                <Trash2 size={13}/> Eliminar todas
              </button>
            )}
          </div>
          {loading && <div className="p-8 text-center text-[#58677A]/60"><Loader2 className="mx-auto animate-spin mb-2"/>Cargando…</div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-[#58677A]/60 text-sm">No hay imágenes. Sube algunas para comenzar.</div>
          )}
          {!loading && items.length > 0 && (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4 sm:p-5">
              {items.map((it, i) => (
                <li
                  key={it.id}
                  className="relative group rounded-xl overflow-hidden border border-[#58677A]/10 bg-[#f4f5f7]"
                  data-testid={`nosotros-row-${it.id}`}
                >
                  <div className="aspect-square w-full overflow-hidden">
                    <img src={toUrl(it.src)} alt="" loading="lazy" className="w-full h-full object-cover"/>
                  </div>
                  {/* Index chip */}
                  <div className="absolute top-1.5 left-1.5 text-[10px] font-mono bg-black/70 text-white rounded px-1.5 py-0.5">#{i + 1}</div>
                  {/* Controls overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-between p-2 opacity-0 group-hover:opacity-100">
                    <div className="flex gap-1">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={reordering || i === 0}
                        className="w-8 h-8 rounded-md bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center"
                        title="Subir"
                        data-testid={`nosotros-up-${it.id}`}
                      ><ArrowUp size={14}/></button>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={reordering || i === items.length - 1}
                        className="w-8 h-8 rounded-md bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center"
                        title="Bajar"
                        data-testid={`nosotros-down-${it.id}`}
                      ><ArrowDown size={14}/></button>
                    </div>
                    <button
                      onClick={() => del(it.id)}
                      className="w-8 h-8 rounded-md bg-white/95 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center"
                      title="Eliminar"
                      data-testid={`nosotros-delete-${it.id}`}
                    ><Trash2 size={14}/></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
